import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Event, NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import {
  Observable,
  Subject,
  combineLatest,
  delay,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import { UserInfoService } from 'src/app/core/services/user-info.service';
import { locales } from '../../constants/Locales';
import { SearchItem } from '../../models/hierarchy.model';
import { AppConfigService } from '../../services/app-config.service';
import { DrawerService } from '../../services/drawer.service';
import { FlagService } from '../../services/flag.service';
import { HierarchyService } from '../../services/hierarchy.service';
import { TenantService } from '../../services/tenant.service';
import { AddOrganizationModalComponent } from '../add-organization-modal/add-organization-modal.component';
import { RegisterGatewayModalComponent } from '../register-gateway-modal/register-gateway-modal.component';

@Component({
  selector: 'sidebar',
  standalone: false,
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.sass']
})
export class SidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('menuTrigger') trigger: any;
  @ViewChild('hierarchyPopoverButton') hierarchyPopoverButton: ElementRef;
  public currentPage$!: Observable<string>;
  private ngUnsubscribe = new Subject<void>();
  public isImgLoaded = false;
  public imgSrc = '';
  public loadingImg = false;
  public name: string | undefined;
  hierarchyTopCoordinate: number;
  fetchingAssetDetails = false;

  linksExpanded = false;
  applicationManagementExpanded = false;

  menuItems: Array<{
    icon: string;
    name: string;
    target: string;
    hide?: boolean;
  }> = [
      {
        icon: 'Home',
        name: $localize`Home`,
        target: ''
      },
      {
        icon: 'Alerts',
        name: $localize`Alerts`,
        target: 'alerts'
      },
      {
        icon: 'Recommendations',
        name: $localize`Recommendations`,
        target: 'recommendations'
      },
      {
        icon: 'Assets',
        name: $localize`Assets`,
        target: 'assets'
      },
      {
        icon: 'Comparisons',
        name: $localize`Comparisons`,
        target: 'comparisons'
      },
      {
        icon: 'Cost Avoidance',
        name: $localize`Cost Avoidance`,
        target: 'cost'
      },
      {
        icon: 'Company Management',
        name: $localize`Company Management`,
        target: 'company',
        hide: true
      },
      {
        icon: 'Sensor Driven Apps',
        name: $localize`Sensor Driven Apps`,
        target: 'sensor-driven-applications',
        hide: true
      },
      {
        icon: 'Devices',
        name: $localize`Devices`,
        target: 'devices'
      },
      {
        icon: 'Hierarchy',
        name: $localize`Manage Hierarchy`,
        target: 'hierarchy'
      },
      {
        icon: 'Reports',
        name: $localize`Reports`,
        target: 'reports',
        hide: true
      }
    ];

  constructor(
    public tenantService: TenantService,
    public drawerService: DrawerService,
    public router: Router,
    public userInfoService: UserInfoService,
    private hierarchyService: HierarchyService,
    public appConfigService: AppConfigService,
    private cdr: ChangeDetectorRef,
    public flagService: FlagService,
    private dialog: MatDialog,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.name = this.hierarchyService.getCompany().name;
    this.currentPage$ = this.router.events.pipe(
      filter((event: Event) => event instanceof NavigationEnd),
      startWith(this.router.url.split('/')[1]),
      map(() => this.router.url.split('/')[1])
    );
    this.hideSideBarLinks();
  }

  ngAfterViewInit() {
    this.hierarchyTopCoordinate = this.getHierarchyTopCoordinate();
    this.cdr.markForCheck();
  }

  navigateToSite({ type, id, assetId }: SearchItem) {
    switch (type) {
      case 'Site':
      case 'Sublocation':
        this.router.navigate(['dashboard', id]);
        break;
      case 'Asset':
        this.router
          .navigateByUrl('/', { skipLocationChange: true })
          .then(() => {
            this.router.navigate(['asset/health', id]);
          });
        break;
      case 'Component':
        this.router
          .navigateByUrl('/', { skipLocationChange: true })
          .then(() => {
            this.router.navigate(['asset/health', assetId]);
          });
        break;
      default:
        this.router.navigate(['/']);
        break;
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  hideSideBarLinks() {
    combineLatest([this.flagService.flags$, this.userInfoService.info$])
      .pipe(
        tap(([flags]) => {
          const [reports, company] = ['reports', 'company'].map(
            (target) =>
              this.menuItems.find((item) => item.target === target) ??
              ({} as any)
          );

          reports.hide = !flags['reports'];
          company.hide = !this.userInfoService.hasPermission(
            'companyManagementPage'
          );
          this.cdr.markForCheck();
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();
  }

  openMenu() {
    this.trigger?.openMenu();
  }

  closeMenu(event: boolean) {
    if (event) {
      this.trigger?.closeMenu();
    }
  }

  getHierarchyTopCoordinate() {
    const hierarchyPopoverButton = this.hierarchyPopoverButton.nativeElement;
    const rect = hierarchyPopoverButton.getBoundingClientRect();

    return rect.top;
  }

  openAddOrganizationModal() {
    const dialog = this.dialog.open(AddOrganizationModalComponent, {
      width: '40rem'
    });

    dialog
      .afterClosed()
      .pipe(
        filter((res: string) => !!res),
        switchMap((res: string) =>
          this.hierarchyService.postCompany(res).pipe(
            delay(1500),
            tap((tenantResponse) => {
              const firstUrlPathSegment =
                window.location.pathname.split('/')[1];
              let redirectUri = window.location.origin;

              if (locales.indexOf(firstUrlPathSegment) > -1) {
                redirectUri += `/${firstUrlPathSegment}`;
              }

              this.authService.loginWithRedirect({
                authorizationParams: {
                  redirect_uri: redirectUri,
                  organization: tenantResponse.obj.organizationid
                },
                appState: {
                  target: '/company/information'
                }
              });
            })
          )
        )
      )
      .subscribe();
  }

  openRegisterGatewayModal() {
    const dialog = this.dialog.open(RegisterGatewayModalComponent, {
      width: '45rem'
    });

    dialog
      .afterClosed()
      .pipe(
        tap((res) => {
          if (res) {
            console.log(res);
          }
        })
      )
      .subscribe();
  }
}
